"use client";

import { c as _c } from "react/compiler-runtime";
import Link from "next/link";
import Image from "next/image";
import type { HeroContent } from "@/lib/types/landing";
import { Section } from "@/components/ui/Section";
import { CircularBadge } from "@/components/landing/CircularBadge";
interface HeroProps {
  content: HeroContent;
  /** URL da imagem do app (hero) - opcional, usa placeholder se não houver */
  appImageSrc: string;
  /** URL do ícone do Apple Store - opcional */
  appStoreIconSrc?: string | null;
}

/**
 * Seção hero da landing: duas colunas na mesma linha.
 * Esquerda: título, subtítulo, CTA e badge.
 * Direita: preview do app (mock-up de celular).
 * Conteúdo injetado via props para testabilidade.
 */
export function Hero(t0) {
  const $ = _c(38);
  const {
    content,
    appImageSrc: t1,
    appStoreIconSrc: t2
  } = t0;
  const appImageSrc = t1 === undefined ? "/images/hero-app.png" : t1;
  const appStoreIconSrc = t2 === undefined ? null : t2;
  const {
    title1,
    title2,
    subtitle,
    ctaLabel,
    ctaHref,
    socialProof
  } = content;
  let t3;
  if ($[0] !== content.topBadge) {
    t3 = <span className={"\n              text-sm\n              uppercase\n              font-bold\n              tracking-wider\n              text-zinc-800\n              text-center\n              md:text-left\n            "}>{content.topBadge}</span>;
    $[0] = content.topBadge;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] !== title2) {
    t4 = title2 && <><br /> {title2}</>;
    $[2] = title2;
    $[3] = t4;
  } else {
    t4 = $[3];
  }
  let t5;
  if ($[4] !== t4 || $[5] !== title1) {
    t5 = <h1 className={"\n              md:text-3xl\n              text-2xl\n              mt-1\n              font-black\n              uppercase\n              text-zinc-900\n              text-center\n              md:text-left\n            "}>{title1} {t4}</h1>;
    $[4] = t4;
    $[5] = title1;
    $[6] = t5;
  } else {
    t5 = $[6];
  }
  let t6;
  if ($[7] !== subtitle) {
    t6 = <p className={"\n              md:text-lg\n              text-md\n              max-w-lg\n              text-zinc-800\n              mt-4\n              text-center\n              md:text-left\n            "}>{subtitle}</p>;
    $[7] = subtitle;
    $[8] = t6;
  } else {
    t6 = $[8];
  }
  let t7;
  if ($[9] !== content.epiphany) {
    t7 = content.epiphany && <p className="text-sm text-zinc-800 mt-1 max-w-lg text-center md:text-left font-medium italic">{content.epiphany}</p>;
    $[9] = content.epiphany;
    $[10] = t7;
  } else {
    t7 = $[10];
  }
  let t8;
  if ($[11] !== appStoreIconSrc) {
    t8 = appStoreIconSrc ? <Image src={appStoreIconSrc} alt={"Dispon\xEDvel na App Store"} width={24} height={24} className="invert" /> : <span className="text-lg" aria-hidden={true}>👉</span>;
    $[11] = appStoreIconSrc;
    $[12] = t8;
  } else {
    t8 = $[12];
  }
  let t9;
  if ($[13] !== ctaLabel) {
    t9 = <span className="font-medium">{ctaLabel}</span>;
    $[13] = ctaLabel;
    $[14] = t9;
  } else {
    t9 = $[14];
  }
  let t10;
  if ($[15] !== ctaHref || $[16] !== t8 || $[17] !== t9) {
    t10 = <Link href={ctaHref} target="_self" rel="noopener noreferrer" onClick={_temp} className="inline-flex w-fit items-center gap-2 rounded-full bg-violet-600 px-6 py-3 text-white transition hover:bg-violet-700">{t8}{t9}</Link>;
    $[15] = ctaHref;
    $[16] = t8;
    $[17] = t9;
    $[18] = t10;
  } else {
    t10 = $[18];
  }
  let t11;
  if ($[19] === Symbol.for("react.memo_cache_sentinel")) {
    t11 = <Image src="/images/arrow.svg" alt="" width={112} height={112} className="rotate-110 md:rotate-180 w-[80px] h-[80px] md:w-[112px] md:h-[112px] hidden md:block" />;
    $[19] = t11;
  } else {
    t11 = $[19];
  }
  let t12;
  if ($[20] !== t10) {
    t12 = <div className="flex items-center md:gap-10 flex-col-reverse md:flex-row">{t10}{t11}</div>;
    $[20] = t10;
    $[21] = t12;
  } else {
    t12 = $[21];
  }
  let t13;
  if ($[22] !== socialProof) {
    t13 = socialProof && <div className="mt-6 flex items-center gap-4"><div className="flex -space-x-3">{socialProof.avatarUrls.map((url, i) => <div key={url} className="relative bg-white h-10 w-10 overflow-hidden rounded-full ring-2 ring-white" style={{
          zIndex: socialProof.avatarUrls.length - i
        }}><Image src={url} alt="" width={40} height={40} className="h-full w-full object-cover" /></div>)}</div><p className="max-w-sm text-sm text-zinc-800"><span className="font-bold text-zinc-900">{socialProof.count}</span>{" "}{socialProof.message}</p></div>;
    $[22] = socialProof;
    $[23] = t13;
  } else {
    t13 = $[23];
  }
  let t14;
  if ($[24] !== t12 || $[25] !== t13) {
    t14 = <div className="mt-8 flex flex-col gap-4">{t12}{t13}</div>;
    $[24] = t12;
    $[25] = t13;
    $[26] = t14;
  } else {
    t14 = $[26];
  }
  let t15;
  if ($[27] !== t14 || $[28] !== t3 || $[29] !== t5 || $[30] !== t6 || $[31] !== t7) {
    t15 = <div className="flex flex-col justify-center text-left">{t3}{t5}{t6}{t7}{t14}</div>;
    $[27] = t14;
    $[28] = t3;
    $[29] = t5;
    $[30] = t6;
    $[31] = t7;
    $[32] = t15;
  } else {
    t15 = $[32];
  }
  let t16;
  if ($[33] !== appImageSrc) {
    t16 = <div className="flex flex-col justify-center lg:justify-end"><div className="relative aspect-9/16 sm:h-[575px] lg:h-[575px]"><Image src={appImageSrc} alt={"Screenshot do aplicativo Di\xE1rio de Cultivo mostrando interface de acompanhamento"} fill={true} className="object-contain object-top" sizes="(max-width: 1024px) 100vw, 50vw" priority={true} fetchPriority="high" /></div></div>;
    $[33] = appImageSrc;
    $[34] = t16;
  } else {
    t16 = $[34];
  }
  let t17;
  if ($[35] !== t15 || $[36] !== t16) {
    t17 = <Section id="hero" aria-label="Hero section"><div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-16 lg:px-12">{t15}{t16}</div></Section>;
    $[35] = t15;
    $[36] = t16;
    $[37] = t17;
  } else {
    t17 = $[37];
  }
  return t17;
}
function _temp() {
  if (typeof window !== "undefined" && window.dataLayer) {
    window.dataLayer.push({
      event: "founders_cta_click"
    });
  }
}